import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Tabs,
  Tab,
  Table,
  Badge,
  ProgressBar,
} from "react-bootstrap";
import {
  collection,
  collectionGroup,
  query,
  where,
  getFirestore,
} from "firebase/firestore";
// getDocs instrumentado: informa cuántas lecturas hace esta pantalla para
// poder atribuir el consumo de Firestore a cada usuario (visible solo para superAdmin).
import { getDocs } from "../../functions/getDocsInstrumentado";
import { establecerOrigenConsumo } from "../../functions/consumoLecturas";
import firebaseApp from "../../firebase/credenciales";
import {
  FaFilter,
  FaUserTimes,
  FaCalendarTimes,
  FaUsers,
  FaChartBar,
  FaMedkit,
  FaHandPaper,
  FaQuestion,
  FaClock,
  FaMale,
  FaFemale,
  FaEllipsisH,
  FaCalendarAlt
} from "react-icons/fa";
import { format } from "date-fns";
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);


const db = getFirestore(firebaseApp);

// Definición de tipos de ausencia
const tiposAusencia = [
  {
    label: "Seleccione tipo de ausencia",
    value: "",
    icon: "help-circle-outline",
    color: "#6B7280",
  },
  {
    label: "PERMISOS",
    value: "Permisos",
    icon: "time-outline",
    color: "#F59E0B",
  },
  {
    label: "DESCANSO MÉDICO",
    value: "DescansoMedico",
    icon: "medical-outline",
    color: "#EF4444",
  },
  {
    label: "LICENCIA PATERNIDAD",
    value: "Paternidad",
    icon: "man-outline",
    color: "#3B82F6",
  },
  {
    label: "LICENCIA MATERNIDAD",
    value: "Maternidad",
    icon: "woman-outline",
    color: "#EC4899",
  },
  {
    label: "VACACIONES",
    value: "Vacaciones",
    icon: "umbrella-beach-outline",
    color: "#10B981",
  },
  {
    label: "OTROS",
    value: "Otros",
    icon: "ellipsis-horizontal-outline",
    color: "#8B5CF6",
  },
];

function IndicadoresAusentismo({ usuario }) {
  const navigate = useNavigate();
  const [rucEmpresa, setRucEmpresa] = useState(usuario?.ruc || "");
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Datos
  const [ausencias, setAusencias] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [tabActivo, setTabActivo] = useState("global");

  // Indicadores
  const [indicadores, setIndicadores] = useState({
    totalAusencias: 0,
    descansos: 0,
    permisos: 0,
    paternidad: 0,
    maternidad: 0,
    otros: 0,
    empleadosAfectados: 0,
    diasAusentes: 0,
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      establecerOrigenConsumo("ausencias:indicadores");
      // Rango de fechas del mes
      const [year, month] = selectedMonth.split("-");
      const fechaInicio = new Date(year, month - 1, 1);
      const fechaFin = new Date(year, month, 0, 23, 59, 59);

      // Buscar empleados activos de la empresa
      const empleadosRef = collection(db, "users");
      const qEmpleados = query(
        empleadosRef,
        where("rucEmpresa", "==", rucEmpresa),
        where("activo", "==", true)
      );
      const snapEmpleados = await getDocs(qEmpleados);
      const empleadosArr = [];
      snapEmpleados.forEach((doc) => empleadosArr.push(doc.data()));

      // Buscar ausencias usando collectionGroup
      const ausenciasQuery = query(
        collectionGroup(db, "ausencias"),
        where("ruc", "==", rucEmpresa),
        where("fechaInicio", "<=", fechaFin)
      );
      const ausenciasSnapshot = await getDocs(ausenciasQuery);

      const ausenciasArr = [];
      const empleadosAfectadosSet = new Set();
      // Indicadores por tipo
      let descansos = 0, permisos = 0, paternidad = 0, maternidad = 0, vacaciones = 0, otros = 0, diasAusentes = 0;

      ausenciasSnapshot.forEach((doc) => {
        const ausencia = doc.data();
        if (ausencia.fechaInicio && ausencia.fechaFin) {
          const ini = ausencia.fechaInicio.seconds
            ? new Date(ausencia.fechaInicio.seconds * 1000)
            : ausencia.fechaInicio.toDate
              ? ausencia.fechaInicio.toDate()
              : new Date(ausencia.fechaInicio);
          const fin = ausencia.fechaFin.seconds
            ? new Date(ausencia.fechaFin.seconds * 1000)
            : ausencia.fechaFin.toDate
              ? ausencia.fechaFin.toDate()
              : new Date(ausencia.fechaFin);

          // Verificar si hay solapamiento con el mes seleccionado
          if (ini <= fechaFin && fin >= fechaInicio) {
            ausenciasArr.push({
              ...ausencia,
              dni: ausencia.dni,
              nombreCompleto: ausencia.nombreCompleto || "",
            });

            empleadosAfectadosSet.add(ausencia.dni);

            // Clasificar tipo según los nuevos tipos
            const tipo = ausencia.tipo || "";
            if (tipo.includes("DescansoMedico") || tipo.toLowerCase().includes("médico") || tipo.toLowerCase().includes("enfermedad")) {
              descansos++;
            } else if (tipo.includes("Permisos") || tipo.toLowerCase().includes("permiso")) {
              permisos++;
            } else if (tipo.includes("Paternidad") || tipo.toLowerCase().includes("paternidad")) {
              paternidad++;
            } else if (tipo.includes("Maternidad") || tipo.toLowerCase().includes("maternidad")) {
              maternidad++;
            } else if (tipo.includes("Vacaciones") || tipo.toLowerCase().includes("vacacion")) {
              vacaciones++;
            } else {
              otros++;
            }

            // Calcular días de ausencia en el mes
            const desde = ini < fechaInicio ? fechaInicio : ini;
            const hasta = fin > fechaFin ? fechaFin : fin;
            const dias = Math.ceil((hasta - desde) / (1000 * 60 * 60 * 24)) + 1;
            diasAusentes += dias > 0 ? dias : 0;
          }
        }
      });

      setAusencias(ausenciasArr);
      setEmpleados(empleadosArr);

      setIndicadores({
        totalAusencias: ausenciasArr.length,
        descansos,
        permisos,
        paternidad,
        maternidad,
        vacaciones,
        otros,
        empleadosAfectados: empleadosAfectadosSet.size,
        diasAusentes,
      });

    } catch (err) {
      console.error("Error al cargar los datos:", err);
      setError("Error al cargar los datos.");
      setAusencias([]);
      setEmpleados([]);
      setIndicadores({
        totalAusencias: 0,
        descansos: 0,
        permisos: 0,
        paternidad: 0,
        maternidad: 0,
        vacaciones: 0,
        otros: 0,
        empleadosAfectados: 0,
        diasAusentes: 0,
      });
    }
    setLoading(false);
  };

  // Función para obtener el color del badge según el tipo
  const getBadgeColor = (tipo) => {
    const tipoLower = tipo.toLowerCase();
    if (tipoLower.includes('médico') || tipoLower.includes('descanso')) return 'danger';
    if (tipoLower.includes('permiso')) return 'warning';
    if (tipoLower.includes('paternidad')) return 'primary';
    if (tipoLower.includes('maternidad')) return 'danger';
    return 'secondary';
  };

  // Calcular porcentajes y métricas adicionales
  const totalEmpleados = empleados.length;
  const porcentajeAfectados = totalEmpleados > 0 ? ((indicadores.empleadosAfectados / totalEmpleados) * 100).toFixed(1) : 0;
  const promedioDiasPorEmpleadoAfectado = indicadores.empleadosAfectados > 0 ? (indicadores.diasAusentes / indicadores.empleadosAfectados).toFixed(1) : 0;

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffffff 0%, #ffffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(45deg, #2c3e50, #3498db)",
              borderBottom: "3px solid #f39c12",
            }}
          >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaChartBar className="me-3" />
                Indicadores de Ausentismo
              </h2>
              <p className="mb-0 opacity-75">
                Análisis integral del ausentismo laboral en tiempo real
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Formulario de filtros */}
            <Card className="shadow-sm border-0 mb-4">
              <Card.Body className="p-4">
                <Row className="align-items-end">
                  <Col md={6} lg={6}>
                    <Form.Group className="mb-3 mb-md-0">
                      <Form.Label className="fw-semibold text-dark">
                        <FaCalendarTimes className="me-2" />
                        Período de Análisis
                      </Form.Label>
                      <Form.Control
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="form-control-lg"
                        disabled={loading}
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6} lg={6}>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={fetchData}
                      disabled={loading}
                      className="w-100"
                      style={{
                        borderRadius: "10px",
                        background: "linear-gradient(45deg, #3498db, #2980b9)",
                        border: "none",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Analizando datos...
                        </>
                      ) : (
                        <>
                          <FaChartBar className="me-2" />
                          Generar Análisis
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md={12}>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        className="flex-grow-1"
                        onClick={() => navigate("/ausencias/admin")}
                        style={{ borderRadius: "10px" }}
                      >
                        <FaCalendarTimes className="me-2" />
                        Gestionar Solicitudes Pendientes
                      </Button>
                      <Button
                        variant="outline-success"
                        className="flex-grow-1"
                        onClick={() => navigate("/ausencias/calendario")}
                        style={{ borderRadius: "10px" }}
                      >
                        <FaCalendarAlt className="me-2" />
                        Ver Calendario de Ausencias
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Error */}
            {error && (
              <Alert
                variant="warning"
                className="border-0 shadow-sm mb-4"
                style={{ borderRadius: "10px" }}
              >
                <div className="d-flex align-items-center">
                  <FaUserTimes className="me-3 fs-4" />
                  <div>
                    <h6 className="fw-bold mb-1">Error en la consulta</h6>
                    <p className="mb-0">{error}</p>
                  </div>
                </div>
              </Alert>
            )}

            {/* Indicadores Principales */}
            {!loading && (indicadores.totalAusencias > 0 || empleados.length > 0) && (
              <Row className="g-3 mb-4">
                {/* Total Ausencias */}
                <Col lg={3} md={6}>
                  <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4 text-center" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
                      <div className="mb-3">
                        <FaUserTimes size={40} className="text-white-50" />
                      </div>
                      <h1 className="display-4 fw-bold mb-2">{indicadores.totalAusencias}</h1>
                      <p className="mb-0 fw-semibold">Total Ausencias</p>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Empleados Afectados */}
                <Col lg={3} md={6}>
                  <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4 text-center" style={{ background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", color: "white" }}>
                      <div className="mb-3">
                        <FaUsers size={40} className="text-white-50" />
                      </div>
                      <h1 className="display-4 fw-bold mb-2">{indicadores.empleadosAfectados}</h1>
                      <p className="mb-0 fw-semibold">Empleados Afectados</p>
                      <small className="text-white-75">
                        {porcentajeAfectados}% del total ({totalEmpleados})
                      </small>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Total Días Ausentes */}
                <Col lg={3} md={6}>
                  <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4 text-center" style={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", color: "white" }}>
                      <div className="mb-3">
                        <FaClock size={40} className="text-white-50" />
                      </div>
                      <h1 className="display-4 fw-bold mb-2">{indicadores.diasAusentes}</h1>
                      <p className="mb-0 fw-semibold">Total Días Ausentes</p>
                      <small className="text-white-75">
                        ⌀ {promedioDiasPorEmpleadoAfectado} días/empleado
                      </small>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Tasa de Ausentismo */}
                <Col lg={3} md={6}>
                  <Card className="h-100 shadow-sm border-0" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4 text-center" style={{ background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)", color: "white" }}>
                      <div className="mb-3">
                        <FaChartBar size={40} className="text-white-50" />
                      </div>
                      <h1 className="display-4 fw-bold mb-2">{porcentajeAfectados}%</h1>
                      <p className="mb-0 fw-semibold">Tasa de Ausentismo</p>
                      <Badge
                        bg={porcentajeAfectados > 15 ? "danger" : porcentajeAfectados > 8 ? "warning" : "success"}
                        className="mt-2"
                      >
                        {porcentajeAfectados > 15 ? "Alta" : porcentajeAfectados > 8 ? "Media" : "Baja"}
                      </Badge>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Análisis Global - Distribución por Tipo */}
            {!loading && indicadores.totalAusencias > 0 && (
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header className="bg-primary text-white py-3">
                  <div className="d-flex align-items-center">
                    <FaMedkit className="me-2" />
                    <h5 className="mb-0 fw-semibold">Distribución por Tipo</h5>
                    <Badge bg="light" text="primary" className="ms-auto">
                      {indicadores.totalAusencias} total
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row>
                    <Col lg={8} md={12}>
                      <div
                        style={{
                          height: "500px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Pie
                          data={{
                            labels: [
                              "Descansos Médicos",
                              "Permisos",
                              "Licencia Paternidad",
                              "Licencia Maternidad",
                              "Vacaciones",
                              "Otros",
                            ],
                            datasets: [
                              {
                                data: [
                                  indicadores.descansos,
                                  indicadores.permisos,
                                  indicadores.paternidad,
                                  indicadores.maternidad,
                                  indicadores.vacaciones || 0,
                                  indicadores.otros,
                                ],
                                backgroundColor: [
                                  "#EF4444", // Descansos Médicos - Rojo
                                  "#F59E0B", // Permisos - Amarillo
                                  "#3B82F6", // Paternidad - Azul
                                  "#EC4899", // Maternidad - Rosa
                                  "#10B981", // Vacaciones - Verde
                                  "#8B5CF6", // Otros - Púrpura
                                ],
                                borderColor: [
                                  "#DC2626",
                                  "#D97706",
                                  "#2563EB",
                                  "#DB2777",
                                  "#059669",
                                  "#7C3AED",
                                ],
                                borderWidth: 2,
                                hoverBackgroundColor: [
                                  "#FCA5A5",
                                  "#FCD34D",
                                  "#93C5FD",
                                  "#F9A8D4",
                                  "#6EE7B7",
                                  "#C4B5FD",
                                ],
                              },
                            ],
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                display: false, // Ocultar la leyenda
                              },
                              tooltip: {
                                callbacks: {
                                  label: function (context) {
                                    const label = context.label || "";
                                    const value = context.raw || 0;
                                    const percentage = (
                                      (value / indicadores.totalAusencias) *
                                      100
                                    ).toFixed(1);
                                    return `${label}: ${value} (${percentage}%)`;
                                  },
                                },
                                backgroundColor: "rgba(0,0,0,0.8)",
                                titleColor: "white",
                                bodyColor: "white",
                                borderColor: "#3498db",
                                borderWidth: 1,
                              },
                              // Plugin para mostrar porcentajes en el gráfico
                              datalabels: {
                                display: true,
                                color: 'white',
                                font: {
                                  weight: 'bold',
                                  size: 12
                                },
                                formatter: (value, context) => {
                                  const percentage = ((value / indicadores.totalAusencias) * 100).toFixed(1);
                                  return percentage > 0 ? `${percentage}%` : '';
                                }
                              }
                            },
                            // Configuración adicional para mostrar porcentajes
                            layout: {
                              padding: 20
                            }
                          }}
                          plugins={[
                            // Plugin personalizado para mostrar porcentajes
                            {
                              id: 'percentageLabels',
                              afterDatasetDraw(chart) {
                                const { ctx } = chart;
                                const meta = chart.getDatasetMeta(0);

                                meta.data.forEach((arc, index) => {
                                  const { x, y } = arc.tooltipPosition();
                                  const value = chart.data.datasets[0].data[index];
                                  const percentage = ((value / indicadores.totalAusencias) * 100).toFixed(1);

                                  if (percentage > 0) {
                                    ctx.save();
                                    ctx.font = 'bold 12px Arial';
                                    ctx.fillStyle = 'white';
                                    ctx.textAlign = 'center';
                                    ctx.textBaseline = 'middle';
                                    ctx.fillText(`${percentage}%`, x, y);
                                    ctx.restore();
                                  }
                                });
                              }
                            }
                          ]}
                        />
                      </div>
                    </Col>
                    <Col lg={4} md={12}>
                      <div className="mt-3 mt-lg-0">
                        <h6 className="fw-bold mb-3">Resumen por Categoría</h6>

                        {/* Descansos Médicos */}
                        <div
                          className="d-flex align-items-center mb-3 p-2 rounded"
                          style={{ backgroundColor: "#FEF2F2" }}
                        >
                          <div
                            className="rounded-circle me-3"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#EF4444",
                            }}
                          ></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">
                                Descansos Médicos
                              </span>
                              <Badge bg="danger">{indicadores.descansos}</Badge>
                            </div>
                            <small className="text-muted">
                              {indicadores.totalAusencias > 0
                                ? (
                                  (indicadores.descansos /
                                    indicadores.totalAusencias) *
                                  100
                                ).toFixed(1)
                                : 0}
                              % del total
                            </small>
                          </div>
                        </div>

                        {/* Permisos */}
                        <div
                          className="d-flex align-items-center mb-3 p-2 rounded"
                          style={{ backgroundColor: "#FFFBEB" }}
                        >
                          <div
                            className="rounded-circle me-3"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#F59E0B",
                            }}
                          ></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">Permisos</span>
                              <Badge bg="warning">{indicadores.permisos}</Badge>
                            </div>
                            <small className="text-muted">
                              {indicadores.totalAusencias > 0
                                ? (
                                  (indicadores.permisos /
                                    indicadores.totalAusencias) *
                                  100
                                ).toFixed(1)
                                : 0}
                              % del total
                            </small>
                          </div>
                        </div>

                        {/* Paternidad */}
                        <div
                          className="d-flex align-items-center mb-3 p-2 rounded"
                          style={{ backgroundColor: "#EFF6FF" }}
                        >
                          <div
                            className="rounded-circle me-3"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#3B82F6",
                            }}
                          ></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">
                                Lic. Paternidad
                              </span>
                              <Badge bg="primary">
                                {indicadores.paternidad}
                              </Badge>
                            </div>
                            <small className="text-muted">
                              {indicadores.totalAusencias > 0
                                ? (
                                  (indicadores.paternidad /
                                    indicadores.totalAusencias) *
                                  100
                                ).toFixed(1)
                                : 0}
                              % del total
                            </small>
                          </div>
                        </div>

                        {/* Maternidad */}
                        <div
                          className="d-flex align-items-center mb-3 p-2 rounded"
                          style={{ backgroundColor: "#FDF2F8" }}
                        >
                          <div
                            className="rounded-circle me-3"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#EC4899",
                            }}
                          ></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">
                                Lic. Maternidad
                              </span>
                              <Badge style={{ backgroundColor: "#EC4899" }}>
                                {indicadores.maternidad}
                              </Badge>
                            </div>
                            <small className="text-muted">
                              {indicadores.totalAusencias > 0
                                ? (
                                  (indicadores.maternidad /
                                    indicadores.totalAusencias) *
                                  100
                                ).toFixed(1)
                                : 0}
                              % del total
                            </small>
                          </div>
                        </div>

                        {/* Vacaciones */}
                        <div
                          className="d-flex align-items-center mb-3 p-2 rounded"
                          style={{ backgroundColor: "#ECFDF5" }}
                        >
                          <div
                            className="rounded-circle me-3"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#10B981",
                            }}
                          ></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">Vacaciones</span>
                              <Badge style={{ backgroundColor: "#10B981" }}>
                                {indicadores.vacaciones || 0}
                              </Badge>
                            </div>
                            <small className="text-muted">
                              {indicadores.totalAusencias > 0
                                ? (
                                  ((indicadores.vacaciones || 0) /
                                    indicadores.totalAusencias) *
                                  100
                                ).toFixed(1)
                                : 0}
                              % del total
                            </small>
                          </div>
                        </div>

                        {/* Otros */}
                        <div
                          className="d-flex align-items-center mb-3 p-2 rounded"
                          style={{ backgroundColor: "#F5F3FF" }}
                        >
                          <div
                            className="rounded-circle me-3"
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "#8B5CF6",
                            }}
                          ></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">Otros</span>
                              <Badge style={{ backgroundColor: "#8B5CF6" }}>
                                {indicadores.otros}
                              </Badge>
                            </div>
                            <small className="text-muted">
                              {indicadores.totalAusencias > 0
                                ? (
                                  (indicadores.otros /
                                    indicadores.totalAusencias) *
                                  100
                                ).toFixed(1)
                                : 0}
                              % del total
                            </small>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}


          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default IndicadoresAusentismo;